import { FC } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, List, SearchField, Text, Title } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import Tooltip from 'modules/core/components/Tooltip'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import { LoadingState } from 'modules/processes/components/LoadingState'
import { useOverviewRoute } from 'modules/processes/hooks/process/useOverviewRoute'
import { Process } from 'modules/processes/hooks/useProcesses'

interface Props {
  category: string
  process: Process
  disableOnboardingProcessesDelete?: boolean
  navigation: Navigation<NavigationPaths>
}

const DeleteOption: FC<Props> = ({
  disableOnboardingProcessesDelete,
  navigation,
  process,
  category,
}) => {
  return (
    <PopoverMenu.RouterOption
      icon="delete"
      disabled={disableOnboardingProcessesDelete}
      title={i18n.t('processes.home.body.company_processes.popover.delete')}
      danger
      to={{
        pathname: navigation.processes.template.delete.fullPath({
          category,
          id: process.id,
        }),
      }}
    />
  )
}

interface ProcessesProps {
  processes: Process[]
  category: string
  search: string
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
  loading: boolean
}

const Processes = ({ processes, category, search, onSearch, loading }: ProcessesProps) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const orderedProcesses = orderBy(processes, (process) => [process.id], ['desc'])
  const route = useOverviewRoute()

  const detailProcessUrl = (process: Process) => {
    return route.fullPath({
      category: category,
      id: process.id,
    })
  }

  const handleClick = () => {
    track(trackingEvents.processes.newCustomWorkflowStarted, {
      category: category,
    })

    navigate(
      navigation.processes.template.new.fullPath({
        category,
      })
    )
  }

  const editProcess = (id: string) => {
    navigate(
      navigation.processes.template.edit.fullPath({
        category,
        id,
      })
    )
  }

  const isCustom = category === 'custom'

  const getCreateLabel = () => {
    if (isCustom || category === 'training') return i18n.t('processes.custom_workflow')
    if (category === 'onboarding') return i18n.t('processes.create_onboarding')
    if (category === 'offboarding') return i18n.t('processes.create_offboarding')
    return i18n.t('processes.create_custom', { name: category })
  }

  const createLabel = getCreateLabel()

  const onboardingProcesses = processes.filter((process) => process.category === 'onboarding')

  const hasProcesses = processes.length !== 0

  const disableOnboardingProcessesDelete = onboardingProcesses.length === 1

  const emptyStates = {
    not_created: {
      title: i18n.t('processes.home.body.company_processes.no_worflows'),
      description: i18n.t('processes.home.body.company_processes.no_workflows_description'),
    },
    not_found: {
      title: i18n.t('processes.home.body.company_processes.search.empty.title'),
      description: i18n.t('processes.home.body.company_processes.search.empty.description'),
    },
  }

  return (
    <Box gap="s24" paddingY="s24">
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center">
        <Title variant="500" title={i18n.t('processes.home.body.company_processes.title')}></Title>
        <Box flexDirection="row" gap="s16">
          <SearchField
            onSearch={onSearch}
            placeholder={i18n.t('processes.home.body.company_processes.search.placeholder')}
            search={search}
            collapsable
          />
          <Button onClick={handleClick} iconName="add">
            {createLabel}
          </Button>
        </Box>
      </Box>
      {loading ? (
        <LoadingState />
      ) : (
        <>
          {!hasProcesses && (
            <Box width="full" alignItems="center" paddingTop="s120">
              <Title
                variant="400"
                title={search ? emptyStates.not_found.title : emptyStates.not_created.title}
              ></Title>

              <Text.Normal color="grey800">
                {search ? emptyStates.not_found.description : emptyStates.not_created.description}
              </Text.Normal>
            </Box>
          )}
          {hasProcesses && (
            <List>
              {orderedProcesses.map((process) => (
                <List.ClickableItem
                  navigateTo={() => navigate(detailProcessUrl(process))}
                  key={process.id}
                >
                  <Text.Normal ellipsis color="grey800">
                    {process.name}
                  </Text.Normal>
                  <Box gap="s32" flexDirection="row" alignItems="center">
                    <List.ActionItem>
                      <Box gap="s6" flexDirection="row">
                        <PopoverMenu type="list">
                          <PopoverMenu.Trigger>
                            <Button.Decorative type="secondary" iconName="ellipsis" />
                          </PopoverMenu.Trigger>
                          <PopoverMenu.Content side="bottom" align="center">
                            <PopoverMenu.TouchableOption
                              icon="pencil"
                              title={i18n.t(
                                'processes.home.body.company_processes.popover.edit_workflow'
                              )}
                              onClick={() => editProcess(process.id)}
                            />
                            <PopoverMenu.RouterOption
                              icon="duplicate"
                              title={i18n.t(
                                'processes.home.body.company_processes.popover.duplicate'
                              )}
                              to={{
                                pathname: navigation.processes.template.duplicate.fullPath({
                                  category,
                                  id: process.id,
                                }),
                              }}
                            />
                            {disableOnboardingProcessesDelete ? (
                              <Tooltip.Alt
                                attachment="top center"
                                label={i18n.t(
                                  'processes.home.body.company_processes.popover.delete_warning'
                                )}
                                textAlign="center"
                              >
                                <DeleteOption
                                  category={category}
                                  process={process}
                                  disableOnboardingProcessesDelete={
                                    disableOnboardingProcessesDelete
                                  }
                                  navigation={navigation}
                                />
                              </Tooltip.Alt>
                            ) : (
                              <DeleteOption
                                category={category}
                                process={process}
                                navigation={navigation}
                              />
                            )}
                          </PopoverMenu.Content>
                        </PopoverMenu>
                        <Button.Router
                          to={detailProcessUrl(process)}
                          type="secondary"
                          iconName="arrow-right"
                        />
                      </Box>
                    </List.ActionItem>
                  </Box>
                </List.ClickableItem>
              ))}
            </List>
          )}
        </>
      )}
    </Box>
  )
}

export default Processes
